import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, ShieldCheck, Sparkles, AlertTriangle,
  XCircle, FileX, GraduationCap, Gavel, Building, AlertOctagon,
  Fingerprint, ShieldOff,
} from 'lucide-react';
import './StudentLoanRemovalPage.css';
import {
  TestimonialCarousel,
  VideoTestimonials,
  DisputeEverywhere,
  Positions250K,
  TailoredPlanForm,
  AgencyMarquee,
} from '../components/ServiceDetailExtras';

const CHECKOUT_URL = 'https://www.commas.com/checkout/L0XZXSgWjqQcXDs8u';
const SIGNUP_URL = 'https://calendly.com/thecollinsacademynew/30min';

const CR_AGENCIES = [
  'Equifax', 'Experian', 'TransUnion',
  'LexisNexis', 'SageStream', 'ARS', 'Innovis', 'CoreLogic',
  'CFPB', 'FTC', 'State AG',
  'Nelnet', 'Navient', 'MOHELA', 'Aidvantage', 'Sallie Mae',
  'Discover', 'SoFi', 'Earnest',
  'Capital One', 'Chase', 'Bank of America', 'Wells Fargo',
  'Portfolio Recovery', 'Midland Credit', 'Enhanced Recovery',
];

const REMOVES = [
  { icon: GraduationCap, label: 'Student loans' },
  { icon: AlertOctagon,  label: 'Bankruptcies' },
  { icon: Gavel,         label: 'Child support arrears' },
  { icon: FileX,         label: 'Judgments & liens' },
  { icon: XCircle,       label: 'Collections' },
  { icon: ShieldOff,     label: 'Late payments' },
  { icon: Building,      label: 'Repossessions & foreclosures' },
  { icon: Fingerprint,   label: 'Hard inquiries' },
  { icon: FileX,         label: 'Charge-offs & public records' },
];

const PAIN_POINTS = [
  'Negative items keeping your score stuck under 700',
  'Mortgage, auto, and business funding applications denied',
  'Interest rates jacked 3-6% above market because of your file',
  'Collections, charge-offs, and late payments years out of date',
  'Student loans, bankruptcies, judgments weighing the file down',
];

const CR_VIDEO_TESTIMONIALS = [
  {
    src: '/testimonials/cr-client-win.mp4',
    quote: '"Credit got cleaned, score jumped, and I\'m finally fundable — on camera."',
    name: 'Verified Client',
    result: 'Credit cleaned on camera',
  },
  {
    src: '/testimonials/client-success.mp4',
    quote: '"The score moved exactly like they said it would — approval the same week."',
    name: 'Verified Client',
    result: 'Funding approval secured',
  },
  {
    src: '/testimonials/mina-voice.mp4',
    quote: "\"Y'all cleared my file in weeks — I'm still in shock. Thank you.\"",
    name: 'Mina',
    result: 'File cleared in weeks',
  },
];

const CreditRepairPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToCheckout = (e) => {
    e.preventDefault();
    const el = document.getElementById('credit-repair-checkout');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="slr-page" data-testid="credit-repair-page">
      {/* HERO */}
      <section className="slr-hero" data-testid="cr-hero">
        <div className="slr-hero-grid">
          <div className="slr-hero-body">
            <span className="slr-eyebrow">
              <Sparkles size={14} /> Complete Credit Repair · 75-Day Guarantee
            </span>
            <h1 className="slr-title">
              Full-service credit repair engineered to <span className="accent">actually move the needle</span> —{' '}
              <span className="gold">in 75 days or your money back.</span>
            </h1>
            <p className="slr-sub">
              Not a template dispute mill. We pull all three reports, audit every line, and
              launch a multi-layer dispute campaign across the bureaus, the original furnishers,
              LexisNexis/SageStream, and CFPB/State AG — until each removable item is gone from
              every source feeding your file.
            </p>

            <div className="slr-hero-stats">
              <div className="slr-stat">
                <div className="slr-stat-num">75</div>
                <div className="slr-stat-lbl">Days to initial results</div>
              </div>
              <div className="slr-stat">
                <div className="slr-stat-num">3</div>
                <div className="slr-stat-lbl">Bureaus + every furnisher</div>
              </div>
              <div className="slr-stat">
                <div className="slr-stat-num">100%</div>
                <div className="slr-stat-lbl">Money-back guarantee</div>
              </div>
            </div>

            <div className="slr-hero-ctas">
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="slr-btn slr-btn-primary slr-btn-lg"
                data-testid="cr-cta-start"
              >
                Sign Up <ArrowRight size={18} />
              </a>
              <Link to="/services" className="slr-btn slr-btn-ghost">
                See All Services
              </Link>
            </div>

            <p className="slr-micro">
              <ShieldCheck size={14} /> CROA / FCRA compliant · Secured checkout · No long-term contracts
            </p>
          </div>
        </div>
      </section>

      {/* CLIENT PHOTO TESTIMONIALS — right under hero */}
      <TestimonialCarousel />

      {/* PAIN */}
      <section className="slr-pain" data-testid="cr-pain">
        <div className="slr-pain-wrap">
          <span className="slr-eyebrow slr-eyebrow-red">
            <AlertTriangle size={14} /> What A Dirty File Costs You
          </span>
          <h2 className="slr-section-title">The real price of leaving negatives on your report.</h2>
          <ul className="slr-pain-list">
            {PAIN_POINTS.map((p, i) => (
              <li key={i} data-testid={`cr-pain-${i}`}>
                <span className="slr-pain-dot" aria-hidden="true" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <p className="slr-pain-note">
            This isn't about dodging what's accurate — it's about forcing the credit system to prove
            every negative item is accurate, complete, and legally compliant. If they can't,
            federal law says it comes off.
          </p>
        </div>
      </section>

      {/* WHAT WE REMOVE */}
      <section className="slr-included" data-testid="cr-removes">
        <div className="slr-included-copy" style={{ textAlign: 'center', maxWidth: 820, margin: '0 auto 24px' }}>
          <span className="slr-eyebrow">What's Included</span>
          <h2 className="slr-section-title">
            Everything negative — <span className="accent">removed.</span>
          </h2>
          <p className="slr-section-sub" style={{ margin: '0 auto 28px' }}>
            No item is off-limits. If it is dragging your score down, we go after it with
            bureau disputes, furnisher challenges, debt validation, statute review, and
            CFPB / State AG escalations.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 12,
          maxWidth: 1100,
          margin: '0 auto',
        }}>
          {REMOVES.map((r, i) => {
            const Icon = r.icon;
            return (
              <div
                key={i}
                data-testid={`cr-remove-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '16px 18px',
                  borderRadius: 12,
                  background: '#fffdf6',
                  border: '1px solid rgba(184, 137, 46, 0.22)',
                  boxShadow: '0 6px 20px -14px rgba(27, 20, 7, 0.3)',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(193, 39, 45, 0.08)',
                  border: '1px solid rgba(193, 39, 45, 0.25)',
                  color: '#c1272d',
                }}>
                  <Icon size={18} />
                </div>
                <span style={{ color: '#0f0f14', fontWeight: 600, fontSize: 14.5 }}>{r.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* MID-PAGE BUY CTA with rotating agency banner */}
      <section className="slr-midbuy" data-testid="cr-midbuy">
        <div className="slr-midbuy-card">
          <div className="slr-midbuy-top">
            <div className="slr-midbuy-copy">
              <span className="slr-eyebrow slr-eyebrow-gold">
                <Sparkles size={13} /> Ready To Move
              </span>
              <h2 className="slr-midbuy-title">
                Seen enough? Start your repair <span className="gold">right now.</span>
              </h2>
              <p className="slr-midbuy-sub">
                75-day money-back guarantee. CROA/FCRA compliant. Secured checkout — intake begins within 24 business hours.
              </p>
            </div>
            <a
              href={SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="slr-btn slr-btn-primary slr-btn-xl"
              data-testid="cr-midbuy-cta"
            >
              Sign Up <ArrowRight size={20} />
            </a>
          </div>
          <AgencyMarquee agencies={CR_AGENCIES} label="Every Bureau · Furnisher · Agency We Pressure" />
        </div>
      </section>

      {/* VIDEO TESTIMONIALS — separated from photo testimonials by the process + removes */}
      <VideoTestimonials items={CR_VIDEO_TESTIMONIALS} />

      {/* GUARANTEE */}
      <section className="slr-guarantee" data-testid="cr-guarantee">
        <div className="slr-guarantee-card">
          <div className="slr-guarantee-badge">
            <ShieldCheck size={34} />
          </div>
          <h2 className="slr-guarantee-title">
            75 Days. Or <span className="gold">your money back.</span>
          </h2>
          <p className="slr-guarantee-body">
            If you do NOT see a single removal in your first 75 days with us, we refund every
            dollar you paid. No arbitration clause. No fine print. No "processing fee" held back.
            Every dollar — refunded.
          </p>
        </div>
      </section>

      {/* DISPUTE EVERYWHERE, $250K FUNDING, FORM */}
      <DisputeEverywhere />
      <Positions250K />
      <TailoredPlanForm
        service="credit-repair"
        serviceLabel="Credit Repair"
      />

      {/* FINAL CHECKOUT */}
      <section className="slr-checkout" id="credit-repair-checkout" data-testid="cr-checkout">
        <div className="slr-checkout-card">
          <span className="slr-eyebrow slr-eyebrow-gold">Final Step</span>
          <h2 className="slr-checkout-title">
            Start your credit repair today.
          </h2>
          <p className="slr-checkout-sub">
            Secure checkout via Commas. Your intake and dispute kickoff begin within 24 business hours.
          </p>
          <a
            href={SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="slr-btn slr-btn-primary slr-btn-xl"
            data-testid="cr-checkout-cta"
          >
            Sign Up <ArrowRight size={20} />
          </a>
          <p className="slr-checkout-note">
            <ShieldCheck size={14} /> 75-day money-back guarantee · Encrypted checkout · CROA/FCRA compliant
          </p>
        </div>
      </section>
    </div>
  );
};

export default CreditRepairPage;
